/**
 * TeamLeader Exam Workspace — Quick Review.
 *
 * One page for the whole manual-grading backlog. Backed by `GET /api/admin/grading-queue` with NO
 * examId, which returns every PendingGrading session the caller is scoped to grade, oldest
 * submission first. That endpoint always applied the caller's grading scope server-side; the only
 * thing missing was a surface that called it unfiltered — until now the queue was only ever mounted
 * inside a single exam's workspace, so clearing a backlog spread over five exams meant walking into
 * five workspaces.
 *
 * The role check is the layout's (`/team-leader` admits TeamLeader and SystemAdmin) plus the
 * endpoint's own `AppRoles.Grading`. Nothing here widens either: a caller who cannot grade a session
 * gets an empty queue, because the same predicate that builds this list is the one the grade call
 * enforces.
 *
 * Filters are deliberately client-side. The queue is one grader's pending work — tens of rows, not
 * thousands — and it arrives whole, so filtering in the browser avoids a refetch per keystroke and
 * keeps the "start a run" snapshot consistent with what is on screen.
 */
import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  CheckCircle2,
  FileText,
  Play,
  RotateCcw,
  Search,
  Users,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TableSkeleton } from "@/components/loading";
import { RapidGradingFlow } from "@/components/team-leader/exams/RapidGradingFlow";
import { keys } from "@/hooks/use-tl-exams";
import { fetchGradingQueue } from "@/lib/api/exams";
import { formatDate } from "@/lib/exam-format";
import type { GradingQueueItem } from "@/lib/types";

export const Route = createFileRoute("/_authenticated/team-leader/exams_/review")({
  component: TeamLeaderQuickReviewPage,
});

const ALL_EXAMS = "__all__";
const labelOf = (s: GradingQueueItem) => s.studentName || s.studentEmail;

/** Exported for tests — the route wrapper above is the only production caller. */
export function TeamLeaderQuickReviewPage() {
  const [examFilter, setExamFilter] = useState<string>(ALL_EXAMS);
  const [search, setSearch] = useState("");
  /** The run in progress: an ordered snapshot, or null while browsing the queue. */
  const [run, setRun] = useState<GradingQueueItem[] | null>(null);

  const {
    data: queue = [],
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery<GradingQueueItem[]>({
    queryKey: keys.gradingQueueAll,
    queryFn: () => fetchGradingQueue(),
  });

  const exams = useMemo(() => {
    const byId = new Map<string, string>();
    for (const q of queue) byId.set(q.examId, q.examTitle || "Untitled exam");
    return [...byId].map(([id, title]) => ({ id, title }));
  }, [queue]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return queue.filter((q) => {
      if (examFilter !== ALL_EXAMS && q.examId !== examFilter) return false;
      if (!term) return true;
      return (
        (q.studentName ?? "").toLowerCase().includes(term) ||
        q.studentEmail.toLowerCase().includes(term) ||
        (q.examTitle ?? "").toLowerCase().includes(term)
      );
    });
  }, [queue, examFilter, search]);

  const answersToMark = filtered.reduce((sum, q) => sum + (q.pendingCount ?? 0), 0);

  // Grouped for display only — a run is still the flat `filtered` order (oldest submission first),
  // so grading follows submission age rather than jumping between exams.
  const grouped = useMemo(() => {
    const map = new Map<string, GradingQueueItem[]>();
    for (const q of filtered) {
      const list = map.get(q.examId) ?? [];
      list.push(q);
      map.set(q.examId, list);
    }
    return [...map].map(([examId, items]) => ({
      examId,
      examTitle: items[0].examTitle || "Untitled exam",
      items,
    }));
  }, [filtered]);

  /** Start a run at a given student, keeping everyone after them in the queue order. */
  const startRun = (fromIndex: number) => {
    const slice = filtered.slice(fromIndex);
    if (slice.length === 0) return;
    setRun(slice);
  };

  const endRun = () => {
    setRun(null);
    // The run only mutated grading state; the queue itself is refetched here so rows that were
    // fully marked (and whose sessions have flipped to Completed) drop off.
    void refetch();
  };

  if (run) {
    return (
      <div className="p-4 md:p-6 space-y-4">
        <RapidGradingFlow sessions={run} onExit={endRun} />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Link
              to="/team-leader/exams"
              className="text-muted-foreground hover:text-foreground"
              aria-label="Back to exams"
            >
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <h1 className="text-xl font-bold">Quick Review</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            Every answer waiting on a mark from you, across all your exams. Grade them in one pass.
          </p>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => void refetch()}>
          <RotateCcw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Attempts waiting</p>
            <p className="text-2xl font-bold">{isLoading ? "—" : filtered.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Answers to mark</p>
            <p className="text-2xl font-bold">{isLoading ? "—" : answersToMark}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Exams involved</p>
            <p className="text-2xl font-bold">{isLoading ? "—" : grouped.length}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4 text-warning" /> Awaiting Your Grading
              </CardTitle>
              <CardDescription>
                {isLoading
                  ? "Loading…"
                  : isError
                    ? "Could not load the queue."
                    : filtered.length === 0
                      ? "Nothing here needs a mark from you."
                      : `${filtered.length} attempt${filtered.length === 1 ? "" : "s"} · ${answersToMark} answer${answersToMark === 1 ? "" : "s"} to mark`}
              </CardDescription>
            </div>
            <Button
              className="gap-1.5"
              disabled={filtered.length === 0}
              onClick={() => startRun(0)}
            >
              <Play className="h-4 w-4" /> Start grading
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder="Search student or exam…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Select value={examFilter} onValueChange={setExamFilter}>
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="All exams" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_EXAMS}>All exams</SelectItem>
                {exams.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4">
              <TableSkeleton rows={4} columns={3} />
            </div>
          ) : isError ? (
            <div className="p-10 text-center space-y-3">
              <p className="text-sm text-muted-foreground">Failed to load the grading queue.</p>
              <Button variant="outline" size="sm" onClick={() => void refetch()}>
                <RotateCcw className="h-4 w-4 mr-1" /> Retry
              </Button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground">
              <CheckCircle2 className="mx-auto mb-2 h-10 w-10 text-success/40" />
              {queue.length === 0
                ? "No attempts are waiting on a mark from you."
                : "No attempts match this filter."}
            </div>
          ) : (
            <div className="divide-y">
              {grouped.map((group) => (
                <div key={group.examId}>
                  <div className="px-4 py-2 bg-muted/30 flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold truncate">{group.examTitle}</p>
                    <Badge variant="outline" className="text-[10px] shrink-0">
                      <Users className="h-3 w-3 mr-1" />
                      {group.items.length}
                    </Badge>
                  </div>
                  {group.items.map((item) => (
                    <div
                      key={item.sessionId}
                      className="px-4 py-2.5 flex flex-wrap items-center justify-between gap-3 border-t"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{labelOf(item)}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {item.studentEmail}
                          {item.submittedAt ? ` · submitted ${formatDate(item.submittedAt)}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge
                          variant="outline"
                          className="text-[10px] bg-warning/15 text-warning border-warning/30"
                        >
                          {item.pendingCount} to mark
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1.5"
                          onClick={() =>
                            startRun(filtered.findIndex((f) => f.sessionId === item.sessionId))
                          }
                        >
                          <Play className="h-3.5 w-3.5" /> Start here
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
